import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Voorkom de NATIVE <details>-toggle op de hamburger-summary: die flipt `open` parallel aan
    // de JS-statebeheer en racet bij snel klikken ('denkt open terwijl dicht'). De JS (open()/#close())
    // beheert `open` + `menu-open` nu volledig en deterministisch.
    this.refs.details?.querySelector('summary')?.addEventListener('click', (event) => event.preventDefault());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   *
   * Bron-van-waarheid = de JS-beheerde `menu-open`-class, NIET het native <details> `open`-attribuut.
   * Op touch-toestellen racet de native toggle met de JS-reset en kan `open` op `true` blijven hangen
   * terwijl de drawer visueel dicht is → tweede+ open faalt. De `menu-open`-class wordt puur door
   * open()/#close()/reset() beheerd en blijft dus betrouwbaar in sync.
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.open = true; // JS beheert de open-staat (native toggle is geprevent)

    this.preventInitialAccordionAnimations(details);
    // menu-open SYNCHROON toevoegen (geen rAF) → geen rAF-venster-race bij snel klikken; de
    // keyframe-instort-animatie speelt sowieso vanaf 'from', dus de rAF-baseline is niet nodig.
    details.classList.add('menu-open');

    // Wait for the drawer animation to complete before trapping focus
    // (dubbele rAF: de open-transitie bestaat pas ná de eerstvolgende style-recalc — zie #close)
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    requestAnimationFrame(() =>
      requestAnimationFrame(() => onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false }))
    );
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // De sluit-transitie bestaat pas ná de eerstvolgende style-recalc; direct getAnimations()
    // aanroepen geeft een lege lijst → reset verwijderde `open` vóór de eerste frame en de drawer
    // klapte ZONDER animatie dicht ("stroef"). Dubbele rAF wacht tot de transitie loopt.
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        onAnimationEnd(
          drawer || details,
          () => {
            if (details.classList.contains('menu-open')) return; // heropend tijdens het sluiten → niet resetten
            reset(details);
            if (details === this.refs.details) {
              removeTrapFocus();
              const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
              openDetails.forEach(reset);
            } else {
              trapFocus(this.refs.details);
            }
          },
          { subtree: false }
        )
      )
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
